import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    Chip,
    Divider
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Event as EventIcon,
    Group as GroupIcon
} from '@mui/icons-material';
import * as http from '../../common/http';
import { APIUrl } from '../../common/apiConfig';

interface UpcomingTestData {
    test_id: number;
    test_name: string;
    test_date: string;
    test_time: string;
    batch_name: string;
    subject_name: string;
    total_students: number;
    duration_minutes: number;
}

const UpcomingTests: React.FC<{ branchId?: number; days?: number }> = ({ 
    branchId, 
    days = 7 
}) => {
    const [data, setData] = useState<UpcomingTestData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let url = `${APIUrl}dashboard-coaching/upcoming-tests?days=${days}`;
            if (branchId) {
                url += `&branch_id=${branchId}`;
            }
            
            const response = await http.get(url);
            if (response && response.status === 'success') {
                setData(response.result || []);
                setLastRefresh(new Date());
            } else {
                setError('Failed to fetch upcoming tests');
            }
        } catch (err) {
            console.error('Error fetching upcoming tests:', err);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [branchId, days]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
        });
    };

    if (loading && data.length === 0) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                        Upcoming Tests
                    </Typography>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchData} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Placeholder message for future feature */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Feature Coming Soon!</strong>
                    </Typography>
                    <Typography variant="caption">
                        Test scheduling feature will be available once the test_schedule table is created.
                        This widget will display upcoming tests with date, batch, subject, and student count.
                    </Typography>
                </Alert>

                {/* Placeholder UI (ready for future implementation) */}
                {data.length > 0 ? (
                    <List>
                        {data.map((test, index) => (
                            <React.Fragment key={test.test_id}>
                                {index > 0 && <Divider />}
                                <ListItem>
                                    <Box sx={{ width: '100%' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {test.test_name}
                                            </Typography>
                                            <Chip 
                                                icon={<EventIcon fontSize="small" />}
                                                label={formatDate(test.test_date)}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Box>
                                        
                                        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                            <Chip 
                                                label={test.subject_name}
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Chip 
                                                label={test.batch_name}
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Chip 
                                                icon={<GroupIcon fontSize="small" />}
                                                label={`${test.total_students} students`}
                                                size="small"
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {test.test_time} • {test.duration_minutes} min
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                ) : (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center"
                        p={4}
                        sx={{ opacity: 0.6 }}
                    >
                        <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            No upcoming tests scheduled
                        </Typography>
                    </Box>
                )}

                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    Last updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default UpcomingTests;
